// Regras puras da tela de Créditos SMS (/creditos-sms).
// Mockup: docs/design/aeros-dark/mockups/Tela Aeros Dark - Créditos SMS.dc.html
import { i18n } from "../../translate/i18n";

export const STATUS_RECARGA = ["paid", "pending", "expired"];

export const statusLabel = (status) =>
  i18n.t(`aeros.sms.status.${STATUS_RECARGA.includes(status) ? status : "unknown"}`);

export const statusTone = (status) => {
  if (status === "paid") return "success";
  if (status === "pending") return "warning";
  return "neutral";
};

/**
 * `valor` vem do backend como string decimal ("170.00"): DECIMAL do Postgres
 * chega assim pelo Sequelize.
 */
export const parseValor = (valor) => {
  const n = Number(valor);
  return Number.isFinite(n) ? n : 0;
};

export const formatBRL = (valor, casas = 2) =>
  parseValor(valor).toLocaleString("pt-BR", {
    style: "currency",
    currency: "BRL",
    minimumFractionDigits: casas,
    maximumFractionDigits: casas,
  });

export const formatMilhar = (n) => Number(n || 0).toLocaleString("pt-BR");

/**
 * Preço por SMS de um pacote — o "R$ 0,089 por SMS" do mockup.
 * Três casas porque a diferença entre pacotes vive na terceira.
 */
export const precoPorSms = (pacote) => {
  const qtd = Number(pacote?.quantidade) || 0;
  if (qtd <= 0) return 0;
  return parseValor(pacote?.valor) / qtd;
};

export const formatPrecoUnitario = (valor) =>
  Number(valor || 0).toLocaleString("pt-BR", {
    style: "currency",
    currency: "BRL",
    minimumFractionDigits: 3,
    maximumFractionDigits: 3,
  });

export const filtraRecargas = (recargas = [], status = "all") => {
  if (!status || status === "all") return recargas;
  return recargas.filter((r) => r.status === status);
};

/**
 * Quanto foi investido no mês corrente: só recargas PAGAS contam — pendente
 * ainda pode expirar sem pagamento, e expirada nunca virou dinheiro.
 */
export const investidoNoMes = (recargas = [], agora = new Date()) =>
  recargas.reduce((soma, r) => {
    if (r.status !== "paid") return soma;
    const data = new Date(r.paidAt || r.createdAt);
    if (Number.isNaN(data.getTime())) return soma;
    if (
      data.getFullYear() !== agora.getFullYear() ||
      data.getMonth() !== agora.getMonth()
    ) {
      return soma;
    }
    return soma + parseValor(r.valor);
  }, 0);

export const recargasNoMes = (recargas = [], agora = new Date()) =>
  recargas.filter((r) => {
    if (r.status !== "paid") return false;
    const data = new Date(r.paidAt || r.createdAt);
    if (Number.isNaN(data.getTime())) return false;
    return (
      data.getFullYear() === agora.getFullYear() &&
      data.getMonth() === agora.getMonth()
    );
  }).length;

/**
 * Em quantos dias o saldo acaba no ritmo atual.
 * Sem consumo não há previsão — devolve null, e a tela omite o aviso em vez
 * de mostrar "acaba em Infinity dias".
 */
export const diasRestantes = (saldo, mediaDiaria) => {
  const s = Number(saldo) || 0;
  const m = Number(mediaDiaria) || 0;
  if (m <= 0 || s <= 0) return null;
  return Math.floor(s / m);
};

// Abaixo de 15 dias de autonomia o aviso aparece — tempo de sobra para uma
// recarga por boleto/PIX cair antes do saldo zerar.
export const DIAS_ALERTA = 15;

export const precisaAlerta = (saldo, mediaDiaria) => {
  const dias = diasRestantes(saldo, mediaDiaria);
  return dias !== null && dias <= DIAS_ALERTA;
};

/**
 * Barras do consumo por dia, normalizadas pela maior do período.
 * A maior barra recebe destaque (gradiente), como no mockup.
 */
export const barrasConsumo = (porDia = []) => {
  const maior = porDia.reduce(
    (max, d) => Math.max(max, Number(d.quantidade) || 0),
    0
  );
  return porDia.map((d) => {
    const valor = Number(d.quantidade) || 0;
    return {
      dia: d.dia,
      valor,
      pct: maior > 0 ? (valor / maior) * 100 : 0,
      destaque: maior > 0 && valor === maior,
    };
  });
};

// "Seg", "Ter"… a partir de YYYY-MM-DD, em data local.
export const rotuloDia = (iso) => {
  if (!/^\d{4}-\d{2}-\d{2}$/.test(String(iso || ""))) return "";
  const [ano, mes, dia] = String(iso).split("-").map(Number);
  const data = new Date(ano, mes - 1, dia);
  return i18n.t(`aeros.sms.weekday.${data.getDay()}`);
};

/**
 * Linhas do card "onde os créditos foram usados", já com percentual e
 * ordenadas da maior para a menor. Origem zerada não vira linha: o card
 * mostra em que os créditos FORAM usados.
 */
export const linhasOrigem = (porOrigem = {}) => {
  const total =
    (Number(porOrigem.campanha) || 0) +
    (Number(porOrigem.automacao) || 0) +
    (Number(porOrigem.manual) || 0) +
    (Number(porOrigem.outros) || 0);

  const linhas = [
    { chave: "campanha", valor: Number(porOrigem.campanha) || 0 },
    { chave: "manual", valor: Number(porOrigem.manual) || 0 },
    { chave: "automacao", valor: Number(porOrigem.automacao) || 0 },
    { chave: "outros", valor: Number(porOrigem.outros) || 0 },
  ];

  return linhas
    .filter((l) => l.valor > 0)
    .map((l) => ({
      ...l,
      label: i18n.t(`aeros.sms.origin.${l.chave}`),
      pct: total > 0 ? (l.valor / total) * 100 : 0,
    }))
    .sort((a, b) => b.valor - a.valor);
};

/**
 * Frações de SMS: 1 até 160 caracteres, blocos de 153 acima disso.
 * Mesma regra do backend (`Fracoes.ts`) — o débito final é dele, este número
 * é a estimativa que a tela mostra antes do envio.
 */
export const calculaFracoes = (texto) => {
  const len = (texto || "").length;
  if (len === 0) return 0;
  if (len <= 160) return 1;
  return Math.ceil(len / 153);
};
